import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Target, ShieldAlert, AlertOctagon, GraduationCap, Users, Truck, Gauge,
  FileText, ClipboardCheck, Smile, Scale, ListTodo, BadgeCheck,
  Camera, Loader2,
} from "lucide-react";
import { LogoMark } from "@/components/brand/Logo";
import { endpoints, getTenantScope } from "@/lib/api";
import { toast } from "sonner";

const initials = (name) => (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

const BRANCHES = [
  { key: "strategic", label: "Strategic Objectives", icon: Target, tone: "indigo", desc: "Company-level ISO objectives and KPIs", route: "/objectives" },
  { key: "risks", label: "Risks & Opportunities", icon: ShieldAlert, tone: "rose", desc: "Risk register and mitigation plans", route: "/risks" },
  { key: "ncrs", label: "NCRs", icon: AlertOctagon, tone: "orange", desc: "Non-conformity reports and root causes", route: "/ncrs" },
  { key: "training", label: "Training", icon: GraduationCap, tone: "emerald", desc: "Training plans and records", route: "/trainings" },
  { key: "employees", label: "Employees & Competency", icon: Users, tone: "sky", desc: "Roles, skills and competency matrices", route: "/employees" },
  { key: "suppliers", label: "Suppliers", icon: Truck, tone: "amber", desc: "Approved supplier list and evaluations", route: "/suppliers" },
  { key: "calibration", label: "Equipment & Calibration", icon: Gauge, tone: "cyan", desc: "Equipment calibration schedule and maintenance records", route: "/equipment" },
  { key: "documents", label: "Documents & Procedures", icon: FileText, tone: "blue", desc: "Controlled documents and SOPs", route: "/documents" },
  { key: "audits_reviews", label: "Audits & Reviews", icon: ClipboardCheck, tone: "purple", desc: "Internal audits, management reviews and findings", route: "/audits-reviews" },
  { key: "customer_sat", label: "Customer Satisfaction", icon: Smile, tone: "pink", desc: "Customer feedback and complaints", route: "/customer-satisfaction" },
  { key: "legal", label: "Legal Requirements", icon: Scale, tone: "slate", desc: "Legal and regulatory compliance register", route: "/legal-requirements" },
  { key: "tasks", label: "ISO Task List", icon: ListTodo, tone: "indigo", desc: "ISO compliance tasks with assignments, evidence and progress", route: "/iso-tasks" },
  { key: "certification", label: "Certification & Audits", icon: BadgeCheck, tone: "purple", desc: "ISO certifications and audit calendar", tab: "certifications" },
];

const TONES = {
  indigo: { grad: "from-indigo-500 to-blue-600", ring: "border-indigo-200 dark:border-indigo-500/30 hover:border-indigo-400 dark:hover:border-indigo-400" },
  rose: { grad: "from-rose-500 to-orange-500", ring: "border-rose-200 dark:border-rose-500/30 hover:border-rose-400" },
  orange: { grad: "from-orange-500 to-amber-500", ring: "border-orange-200 dark:border-orange-500/30 hover:border-orange-400" },
  emerald: { grad: "from-emerald-500 to-teal-500", ring: "border-emerald-200 dark:border-emerald-500/30 hover:border-emerald-400" },
  sky: { grad: "from-sky-500 to-blue-500", ring: "border-sky-200 dark:border-sky-500/30 hover:border-sky-400" },
  amber: { grad: "from-amber-500 to-orange-500", ring: "border-amber-200 dark:border-amber-500/30 hover:border-amber-400" },
  cyan: { grad: "from-cyan-500 to-sky-500", ring: "border-cyan-200 dark:border-cyan-500/30 hover:border-cyan-400" },
  blue: { grad: "from-blue-500 to-indigo-500", ring: "border-blue-200 dark:border-blue-500/30 hover:border-blue-400" },
  purple: { grad: "from-purple-500 to-fuchsia-500", ring: "border-purple-200 dark:border-purple-500/30 hover:border-purple-400" },
  violet: { grad: "from-violet-500 to-purple-500", ring: "border-violet-200 dark:border-violet-500/30 hover:border-violet-400" },
  pink: { grad: "from-pink-500 to-rose-500", ring: "border-pink-200 dark:border-pink-500/30 hover:border-pink-400" },
  slate: { grad: "from-slate-500 to-slate-700", ring: "border-slate-200 dark:border-slate-500/30 hover:border-slate-400" },
  teal: { grad: "from-teal-500 to-emerald-500", ring: "border-teal-200 dark:border-teal-500/30 hover:border-teal-400" },
};

const RADIUS = 40; // % of container width/height (container is square)
const HUB_RADIUS = 12; // % where center circle ends

export default function HubSpokeMap({ client, onNavigate, onClientChanged }) {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const mobileFileRef = useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const scope = getTenantScope();
  const canEditLogo = !scope || scope.role === "client_admin" || (scope.role === "client" && scope.slug === client.slug) === false;
  // Simplified: allow when scope is null (workspace admin/staff) OR scope role is client_admin for this tenant.
  const canUploadLogo = !scope || scope.role === "client_admin";

  const pickLogo = (ref) => { if (ref?.current) ref.current.value = ""; ref?.current?.click(); };
  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be ≤ 2 MB"); return; }
    setUploading(true);
    try {
      const updated = await endpoints.uploadClientLogo(client.slug, file);
      toast.success("Logo updated");
      onClientChanged?.(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not upload logo");
    } finally { setUploading(false); }
  };
  const clearLogo = async () => {
    if (!window.confirm("Remove the current logo?")) return;
    setUploading(true);
    try {
      const updated = await endpoints.clearClientLogo(client.slug);
      toast.success("Logo removed");
      onClientChanged?.(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove logo");
    } finally { setUploading(false); }
  };

  const handleClick = (b) => {
    if (b.route) {
      navigate(`${b.route}?client=${client.slug}`);
    } else if (b.tab) {
      onNavigate(b.tab);
    } else {
      toast(`${b.label} · coming soon`, { description: b.desc });
    }
  };

  const points = BRANCHES.map((b, i) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / BRANCHES.length;
    return {
      ...b,
      angle,
      cos: Math.cos(angle),
      sin: Math.sin(angle),
      x: 50 + RADIUS * Math.cos(angle),
      y: 50 + RADIUS * Math.sin(angle),
    };
  });

  return (
    <div data-testid="client-hub-map" className="relative">
      {/* Ambient gradient backdrop */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 via-white/30 to-purple-50/50 dark:from-indigo-950/30 dark:via-slate-900/40 dark:to-purple-950/30 rounded-3xl" />
      </div>

      {/* Header: "Managed by ISOQuest" */}
      <div className="flex items-center justify-between px-4 py-3 mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <LogoMark size={28} />
          <div className="leading-tight">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Managed by</div>
            <div className="font-display font-extrabold text-sm">
              ISO<span className="brand-gradient-text">Quest</span>
              <span className="ml-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-widest">ISO System</span>
            </div>
          </div>
        </div>
        <div className="text-right leading-tight">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Your ISO command centre</div>
          <div data-testid="hub-company-name" className="font-display font-extrabold text-sm truncate max-w-[220px]">{client.company_name}</div>
        </div>
      </div>

      {/* DESKTOP: circular hub-and-spoke */}
      <div className="hidden md:block relative w-full mx-auto" style={{ maxWidth: 920, aspectRatio: "1 / 1" }}>
        {/* SVG connector lines + rings */}
        <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none">
          <defs>
            <radialGradient id="hub-halo" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#7C3AED" stopOpacity="0.15" />
              <stop offset="60%" stopColor="#4F46E5" stopOpacity="0.06" />
              <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="spoke-line" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#7C3AED" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#2563EB" stopOpacity="0.12" />
            </linearGradient>
          </defs>
          {/* Ambient halo */}
          <circle cx="50" cy="50" r="46" fill="url(#hub-halo)" />
          {/* Outer + middle dashed rings */}
          <circle cx="50" cy="50" r={RADIUS + 4} fill="none" stroke="rgba(124,58,237,0.14)" strokeWidth="0.2" strokeDasharray="0.6 1.2" />
          <circle cx="50" cy="50" r={RADIUS - 6} fill="none" stroke="rgba(37,99,235,0.10)" strokeWidth="0.15" strokeDasharray="0.3 1" />
          {/* Spokes */}
          {points.map((p) => {
            // start just outside the hub circle so the line doesn't clip through the logo
            const x1 = 50 + HUB_RADIUS * p.cos;
            const y1 = 50 + HUB_RADIUS * p.sin;
            // end just before the branch card
            const x2 = 50 + (RADIUS - 4) * p.cos;
            const y2 = 50 + (RADIUS - 4) * p.sin;
            return (
              <g key={p.key}>
                <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="url(#spoke-line)" strokeWidth="0.35" strokeLinecap="round" />
                <circle cx={x2} cy={y2} r="0.6" fill="#7C3AED" opacity="0.55" />
              </g>
            );
          })}
        </svg>

        {/* Center hub with client logo */}
        <div
          data-testid="hub-center"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
        >
          <div className="relative group">
            <div className="h-44 w-44 lg:h-48 lg:w-48 rounded-full brand-gradient p-[3px] shadow-2xl shadow-indigo-500/40 animate-[pulse_6s_ease-in-out_infinite]">
              <div className="h-full w-full rounded-full bg-white dark:bg-slate-900 flex items-center justify-center overflow-hidden ring-4 ring-white/70 dark:ring-slate-900/70 relative">
                {client.logo_url ? (
                  <img src={client.logo_url} alt={client.company_name} className="h-full w-full object-contain p-3" />
                ) : (
                  <div className="text-5xl font-extrabold brand-gradient-text font-display">
                    {initials(client.company_name)}
                  </div>
                )}
                {canUploadLogo && (
                  <button
                    type="button"
                    data-testid="hub-logo-upload"
                    onClick={() => pickLogo(fileRef)}
                    disabled={uploading}
                    className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-slate-900/55 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity rounded-full disabled:opacity-70 disabled:cursor-wait"
                    title={client.logo_url ? "Replace client logo" : "Upload client logo"}
                  >
                    {uploading
                      ? <Loader2 className="h-6 w-6 animate-spin" />
                      : <Camera className="h-6 w-6" />}
                    <span className="text-[10px] font-semibold uppercase tracking-widest">{client.logo_url ? "Replace logo" : "Upload logo"}</span>
                  </button>
                )}
              </div>
            </div>
            {canUploadLogo && (
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif" className="hidden" onChange={onLogoFile} data-testid="hub-logo-file" />
            )}
            {canUploadLogo && client.logo_url && (
              <button
                type="button"
                onClick={clearLogo}
                data-testid="hub-logo-clear"
                className="absolute -top-1 -right-1 h-7 w-7 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-white/10 shadow text-slate-500 hover:text-rose-600 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity flex items-center justify-center text-xs font-bold"
                title="Remove logo"
              >
                ×
              </button>
            )}
            {/* Managed-by pill */}
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap px-3 py-1.5 rounded-full bg-white dark:bg-slate-800 shadow-lg border border-slate-200 dark:border-white/10 flex items-center gap-1.5">
              <LogoMark size={14} />
              <span className="text-[10px] font-bold tracking-wide">
                Managed by <span className="brand-gradient-text">ISOQuest</span>
              </span>
            </div>
          </div>
          <div className="mt-6 text-center">
            <div className="font-display font-extrabold text-[15px] tracking-tight max-w-[220px] truncate">{client.company_name}</div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400 mt-0.5">ISO Management Hub</div>
          </div>
        </div>

        {/* Branch nodes */}
        {points.map((p) => {
          const Icon = p.icon;
          const t = TONES[p.tone] || TONES.indigo;
          // Nudge labels away from the center so they don't collide with the hub visually
          const isLeft = p.cos < -0.35;
          const isRight = p.cos > 0.35;
          const align = isLeft ? "flex-row-reverse text-right" : "flex-row text-left";
          return (
            <button
              key={p.key}
              data-testid={`hub-branch-${p.key}`}
              onClick={() => handleClick(p)}
              style={{ left: `${p.x}%`, top: `${p.y}%`, transform: "translate(-50%, -50%)" }}
              className={`absolute group flex ${align} items-center gap-2 rounded-2xl bg-white/95 dark:bg-slate-900/85 backdrop-blur-md border ${t.ring} pl-1.5 pr-3 py-1.5 shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200`}
              title={p.desc}
            >
              <div className={`h-9 w-9 rounded-xl bg-gradient-to-br ${t.grad} text-white flex items-center justify-center shrink-0 shadow-md shadow-indigo-500/25 group-hover:scale-110 transition-transform`}>
                <Icon className="h-4 w-4" />
              </div>
              <span className={`text-[11px] font-semibold leading-tight max-w-[110px] ${isRight || isLeft ? "" : "text-center"}`}>
                {p.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* MOBILE / TABLET fallback: hub image + grid */}
      <div className="md:hidden">
        <div className="relative w-full flex flex-col items-center py-6">
          <div className="relative group">
            <div className="h-32 w-32 rounded-full brand-gradient p-[3px] shadow-xl shadow-indigo-500/40 mb-4">
              <div className="h-full w-full rounded-full bg-white dark:bg-slate-900 flex items-center justify-center overflow-hidden relative">
                {client.logo_url ? (
                  <img src={client.logo_url} alt={client.company_name} className="h-full w-full object-contain p-2" />
                ) : (
                  <div className="text-3xl font-extrabold brand-gradient-text">{initials(client.company_name)}</div>
                )}
                {canUploadLogo && (
                  <button
                    type="button"
                    onClick={() => pickLogo(mobileFileRef)}
                    disabled={uploading}
                    data-testid="hub-logo-upload-mobile"
                    className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-slate-900/55 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 active:opacity-100 transition-opacity rounded-full"
                  >
                    {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
                    <span className="text-[9px] font-semibold uppercase tracking-widest">{client.logo_url ? "Replace" : "Upload"}</span>
                  </button>
                )}
              </div>
            </div>
            {canUploadLogo && (
              <input ref={mobileFileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif" className="hidden" onChange={onLogoFile} />
            )}
          </div>
          <div className="whitespace-nowrap px-3 py-1 rounded-full bg-white dark:bg-slate-800 shadow border border-slate-200 dark:border-white/10 flex items-center gap-1.5 mb-2">
            <LogoMark size={12} />
            <span className="text-[10px] font-bold">Managed by <span className="brand-gradient-text">ISOQuest</span></span>
          </div>
          <div className="font-display font-extrabold text-sm">{client.company_name}</div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mt-0.5">ISO Management Hub</div>
        </div>
        <div className="grid grid-cols-2 gap-3 pt-2">
          {BRANCHES.map((b) => {
            const Icon = b.icon;
            const t = TONES[b.tone] || TONES.indigo;
            return (
              <button
                key={b.key}
                data-testid={`hub-branch-mobile-${b.key}`}
                onClick={() => handleClick(b)}
                className={`flex items-center gap-2 rounded-2xl bg-white/90 dark:bg-slate-900/85 backdrop-blur-md border ${t.ring} px-3 py-2.5 text-left`}
              >
                <div className={`h-8 w-8 rounded-xl bg-gradient-to-br ${t.grad} text-white flex items-center justify-center shrink-0`}>
                  <Icon className="h-4 w-4" />
                </div>
                <span className="text-[11px] font-semibold leading-tight">{b.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
